import React, { useState } from 'react';
import { Headphones, TriangleAlert as AlertTriangle, ArrowRight, CircleCheck as CheckCircle2, Mic, RefreshCw } from 'lucide-react';

interface KioskHeadsetCheckProps {
  accentColor?: string;
  primaryColor?: string;
  onContinue: (preferredDeviceId?: string, liveStream?: MediaStream) => void;
}

interface DetectedDevice {
  deviceId: string;
  label: string;
  isExternal: boolean;
}

export const KioskHeadsetCheck: React.FC<KioskHeadsetCheckProps> = ({
  accentColor = '#f97316',
  primaryColor = '#f97316',
  onContinue,
}) => {
  const [devices, setDevices] = useState<DetectedDevice[]>([]);
  const [detecting, setDetecting] = useState(false);
  const [hasDetected, setHasDetected] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [liveStream, setLiveStream] = useState<MediaStream | null>(null);

  const detectDevices = async () => {
    setDetecting(true);
    try {
      // Release any previous live stream before re-detecting
      if (liveStream) {
        liveStream.getTracks().forEach((t) => t.stop());
        setLiveStream(null);
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setLiveStream(stream);

      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = allDevices.filter((d) => d.kind === 'audioinput');

      const mapped: DetectedDevice[] = audioInputs
        .filter((d) => d.deviceId !== 'default' && d.deviceId !== 'communications')
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || 'Unknown device',
          isExternal:
            !/built-in|internal|iphone microphone|ipad microphone/i.test(d.label) && d.label !== '',
        }));

      setDevices(mapped);
      setHasDetected(true);

      // Auto-select external if available and nothing selected yet
      if (!selectedDeviceId || !mapped.some((d) => d.deviceId === selectedDeviceId)) {
        const external = mapped.find((d) => d.isExternal);
        if (external) {
          setSelectedDeviceId(external.deviceId);
        } else if (mapped.length > 0) {
          setSelectedDeviceId(mapped[0].deviceId);
        }
      }
    } catch {
      setDevices([]);
      setHasDetected(true);
    }
    setDetecting(false);
  };

  const handleSelectDevice = async (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    // Re-acquire stream for the selected device so it stays warm
    if (liveStream) {
      liveStream.getTracks().forEach((t) => t.stop());
      setLiveStream(null);
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId }, echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      setLiveStream(stream);
    } catch {
      // If exact fails, keep null -- recorder will retry on its own
    }
  };

  const handleContinue = () => {
    // Pass the live stream to avoid re-acquisition race on Bluetooth devices
    onContinue(selectedDeviceId || undefined, liveStream || undefined);
  };

  const selectedDevice = devices.find((d) => d.deviceId === selectedDeviceId);

  return (
    <div className="text-center py-1">
      <div
        className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3"
        style={{ backgroundColor: `${accentColor}22`, border: `1px solid ${accentColor}55` }}
      >
        <Headphones className="w-6 h-6" style={{ color: accentColor }} />
      </div>

      <h2 className="text-lg sm:text-xl font-black mb-1.5">Choose your microphone</h2>
      <p className="text-white/70 text-sm max-w-md mx-auto leading-snug">
        Plug in your headset, then tap the button below to detect it. Select which mic input to use for recording.
      </p>

      {/* Detect button */}
      <div className="mt-4 mx-auto max-w-md">
        <button
          onClick={detectDevices}
          disabled={detecting}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition border ${
            detecting
              ? 'bg-white/5 border-white/10 text-white/40 cursor-not-allowed'
              : 'bg-white/10 border-white/20 text-white hover:bg-white/15 hover:border-white/30'
          }`}
        >
          <RefreshCw className={`w-4 h-4 ${detecting ? 'animate-spin' : ''}`} />
          {detecting ? 'Detecting...' : hasDetected ? 'Re-detect Devices' : 'Detect Audio Devices'}
        </button>
      </div>

      {/* Device list */}
      {hasDetected && (
        <div className="mt-4 mx-auto max-w-md">
          {devices.length > 0 ? (
            <div className="text-left">
              <label className="text-white/50 text-[10px] uppercase tracking-wider mb-1.5 block px-1">
                Select recording input
              </label>
              <div className="space-y-1.5">
                {devices.map((d) => {
                  const isSelected = d.deviceId === selectedDeviceId;
                  return (
                    <button
                      key={d.deviceId}
                      onClick={() => handleSelectDevice(d.deviceId)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left text-xs transition ${
                        isSelected
                          ? 'bg-emerald-500/10 border-2 border-emerald-400/50 text-emerald-100'
                          : 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 hover:border-white/20'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'bg-emerald-500/20' : 'bg-white/10'
                      }`}>
                        <Mic className={`w-4 h-4 ${isSelected ? 'text-emerald-400' : 'text-white/50'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate ${isSelected ? 'text-emerald-100' : 'text-white/80'}`}>
                          {d.label}
                        </p>
                        {d.isExternal && (
                          <p className="text-[10px] text-emerald-400/80 mt-0.5">External device</p>
                        )}
                      </div>
                      {isSelected && (
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-400/30 text-left">
              <p className="text-amber-200 text-xs">No audio inputs found. Check that your browser has microphone permission and try again.</p>
            </div>
          )}
        </div>
      )}

      {/* Tip */}
      <div className="mt-3 mx-auto max-w-md flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-400/30 text-left">
        <AlertTriangle className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
        <div className="text-amber-100/90 text-xs leading-snug">
          Plug in your headset first, then tap "Detect Audio Devices". Wired earbuds or Bluetooth both work.
        </div>
      </div>

      {/* Selected device confirmation */}
      {selectedDevice && (
        <div className="mt-3 mx-auto max-w-md flex items-center gap-2 p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
          <span className="text-emerald-200 text-[11px]">
            Will record using: <span className="font-semibold">{selectedDevice.label}</span>
          </span>
        </div>
      )}

      <button
        onClick={handleContinue}
        disabled={detecting}
        className={`mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white shadow-lg transition ${
          !detecting ? 'hover:brightness-110' : 'opacity-40 cursor-not-allowed'
        }`}
        style={{ backgroundColor: primaryColor }}
      >
        I'm ready - continue
        <ArrowRight className="w-4 h-4" />
      </button>

      <p className="text-[11px] text-white/50 mt-2">
        Select your preferred mic above, then continue.
      </p>
    </div>
  );
};

export default KioskHeadsetCheck;
